var Component              = require("enyo/Component"),
    WatchingSessionService = require("app/Domain/Ott/Api/V2/DataAccess/WatchingSession/Service"),
    get                    = require("object-path").get,
    kind                   = require("enyo/kind"),
    logger                 = require("enyo/logger"),
    utils                  = require("enyo/utils");

module.exports = kind(/** @lends  app.WatchingSession.WatchingSessionService.prototype */ {

    /**
     * @private
     */
    name: "app.WatchingSession.AuthService",

    /**
     * @private
     */
    kind: Component,

    /**
     * The current session status. Status can have the following values:
     *  - `-1` = no session
     *  -  `0` = status is still currently being determined
     *  -  `1` = active session
     *
     * @type     {number}
     * @default  -1
     * @readonly
     * @public
     */
    hasSession: -1,

    /**
     * The current session
     *
     * @type  {?Object}
     * @readonly
     * @public
     */
    session: null,

    // ----------------------------------
    // Get current session parameters
    // ----------------------------------

    /**
     * Determine if the current session has a ping endpoint for us to submit
     * watching progress to.
     *
     * @returns  {boolean}  Whether we have a ping endpoint
     * @public
     */
    hasPingEndpoint: function() {
        var session = this.session || {};

        if (this.hasSession !== 1) {
            return false;
        }

        return session.ping_endpoint ? true : false;
    },

    /**
     * Figure out whether this session is anonymous.
     *
     * @returns  {?boolean}  Whether the session is anonymous.
     * @public
     */
    isAnonymous: function() {
        var session = this.session || {};

        return session.user_id ? false : true;
    },

    /**
     * Figure out whether this session identifies a particular user.
     *
     * @returns  {?boolean}  Whether the session identifies a particular user
     * @public
     */
    isIdentified: function() {
        return !this.isAnonymous();
    },

    /**
     * Get the cursor position as stored in the current session.
     *
     * @return  {?number}  The current cursor position, or `null` if
     *                     unavailable.
     * @public
     */
    getCursor: function() {
        var session = this.session || {};

        if (typeof session.cursor === "number") {
            return session.cursor;
        }
        else {
            return null;
        }
    },

    // ----------------------------------
    // Set session state
    // ----------------------------------

    /**
     * Mark session status as pending.
     *
     * @private
     */
    setSessionPending: function() {
        if (this.hasSession !== 0) {
            this.set("hasSession", 0);
        }
    },

    /**
     * Marks session as active, this also commits new `session` object.
     *
     * @param  {Object}  session  The session data
     * @private
     */
    setSessionActive: function(session) {
        if (this.hasSession !== 1) {
            this.set("session", session);
            this.set("hasSession", 1);
        }
    },

    /**
     * Marks session as destroyed, this also clears the `session` object.
     *
     * @private
     */
    setSessionDestroyed: function() {
        if (this.hasSession !== -1) {
            this.set("session", null);
            this.set("hasSession", -1);
        }
    },

    // ----------------------------------
    // Session authentication
    // ----------------------------------

    /**
     * Request a new session by providing a media type and media slug.
     *
     * @param    {string}   language    The language
     * @param    {string}   mediaType   The media type
     * @param    {string=}  mediaSlug   The media slug
     * @param    {string=}  mediaId     The media id
     * @param    {string=}  categoryId  Optional category id
     * @param    {string=}  isAdult     Optional age flag
     * @returns  {Promise}  A Promise that resolves with the API response
     * @public
     */
    requestSession: function(language, mediaType, mediaSlug, mediaId, categoryId, isAdult) {
        var app               = this.app,
            $analyticsService = app.$.analyticsService,
            sessionPromise;

        // Set session state to pending
        this.setSessionPending();

        if (mediaSlug) {
            sessionPromise = WatchingSessionService.requestBySlug(language, mediaType, mediaSlug, categoryId, isAdult);
        }
        else {
            sessionPromise = WatchingSessionService.requestById(language, mediaType, mediaId, categoryId, isAdult);
        }

        return sessionPromise
            .then(this.bindSafely(function(response) {
                switch (response.status) {

                    // Session was created, store state and resolve.
                    case "ok":
                        this.setSessionActive(response.data);
                        $analyticsService.sendEvent(
                            ["accepted", "analyticsCookies"],
                            "Watching Session",
                            "new_watching_session_success",
                            "ok",
                            null,
                            {
                                language:  language,
                                mediaItem: [mediaType.replaceAll("_", "-"), mediaId, mediaSlug].filter(function(v) {
                                    return v;
                                }).join("/")
                            }
                        );

                        return Promise.resolve({
                            status: "ok",
                            data:   this.get("session")
                        });

                    // Authentication failed
                    case "error":
                    default:
                        this.setSessionDestroyed();
                        $analyticsService.sendEvent(
                            ["accepted", "analyticsCookies"],
                            "Watching Session",
                            "new_watching_session_fail",
                            response.type || "unknown_error",
                            null,
                            {
                                language:  language,
                                mediaItem: [mediaType.replaceAll("_", "-"), mediaId, mediaSlug].filter(function(v) {
                                    return v;
                                }).join("/")
                            }
                        );

                        return Promise.resolve({
                            status: "error",
                            type:   response.type || "unknown_error"
                        });
                }
            }))
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                this.setSessionDestroyed();
                $analyticsService.sendEvent(
                    ["accepted", "analyticsCookies"],
                    "Watching Session",
                    "new_watching_session_fail",
                    error.message || "unknown_error",
                    null,
                    {
                        language:  language,
                        mediaItem: [mediaType.replaceAll("_", "-"), mediaId, mediaSlug].filter(function(v) {
                            return v;
                        }).join("/")
                    }
                );

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            }));
    },

    /**
     * Updates the cursor position for the current session.
     *
     * @param    {number}   cursor  The cursor position
     * @returns  {Promise}  A Promise that resolves with the API response
     * @public
     */
    updateCursor: function(cursor) {
        var pingEndpoint = get(this, "session.ping_endpoint", null);

        if (!pingEndpoint) {
            return Promise.resolve({
                status: "error",
                type:   "missing_ping_endpoint"
            });
        }

        return WatchingSessionService.updateCursor(cursor, pingEndpoint)
            .then(this.bindSafely(function(response) {
                var session;

                if (response.status === "ok") {
                    // Update local session with accepted cursor value
                    if (this.session) {
                        session        = utils.clone(this.session);
                        session.cursor = cursor;
                        this.set("session", session);
                    }

                    return Promise.resolve({
                        status: "ok"
                    });
                }
                else if (response.status === "canceled") {
                    return Promise.resolve({
                        status: "canceled"
                    });
                }
                else {
                    this.setSessionDestroyed();

                    return Promise.resolve({
                        status: "error",
                        type:   response.type || "unknown_error"
                    });
                }
            }))
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                this.setSessionDestroyed();

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            }));
    },

    /**
     * Stops the current watching session.
     *
     * @returns  {Object}  An object with `async` flag and a Promise that
     *                     resolves with the API response
     * @public
     */
    stopSession: function() {
        this.setSessionDestroyed();

        return Promise.resolve({
            status: "ok"
        });
    }

});
